/**
 * apps/web :: lib/pass-content
 *
 * Thin app-side adapter over the shared `@roadbridge/pass-content`
 * package. Re-exports the pack accessors + types so pass screens import
 * from one place, and adds the `useActivePassCountries()` hook that maps
 * the user's active `tourist_out` entitlements to pack ids.
 */
import { getPack, listPacks, PACKS } from "@roadbridge/pass-content";
import type {
  PackId,
  TravelerPack,
  Rule,
  RuleCategory,
  Severity,
  Jurisdiction,
  JurisdictionType,
  QuickFacts,
  ChecklistItem,
  TripChecklists,
  Lesson,
  Course,
  EmergencyCard,
} from "@roadbridge/pass-content";

import { useEntitlement } from "@/entitlement/EntitlementGateContext";

export { getPack, listPacks, PACKS };
export type {
  PackId,
  TravelerPack,
  Rule,
  RuleCategory,
  Severity,
  Jurisdiction,
  JurisdictionType,
  QuickFacts,
  ChecklistItem,
  TripChecklists,
  Lesson,
  Course,
  EmergencyCard,
};

/**
 * Country codes (uppercased, to match pack ids like "IT") of the user's
 * active `tourist_out` entitlements. Reads the entitlement gate context
 * so it only returns countries the user actually paid for / is trialing.
 *
 * De-duplicated and order-preserving. Returns `[]` when no provider is
 * mounted (the gate fails open with an empty active list).
 */
export function useActivePassCountries(): string[] {
  const { activeEntitlements } = useEntitlement();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const ent of activeEntitlements) {
    if (ent.tier !== "tourist_out") continue;
    if (!ent.country_code) continue;
    const code = ent.country_code.toUpperCase();
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}
